import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Calendar, ChevronRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { SessionCancellationNotice } from '@/components/session/SessionCancellationNotice';
import {
	getSessionCallCompanyBanner,
	isSessionTranscriptionAllowed,
} from '@/constants/sessionCall';
import { useProfessionalSessionAccessWindow } from '@/hooks/useProfessionalSessionAccessWindow';
import { openProfessionalSessionCall } from '@/utils/professionalSessionCall';
import { isP2PTranscriptionOnlySessionType } from '@/utils/sessionType';
import { buildProfessionalSessionEditUrlEncoded } from '@/pages/professional/sessions/utils/professionalSessionEditNavigation';
import { professionalHasP2PPermission } from '@/utils/professionalPermissionTags';
import { getProfessionalData } from '@/utils/cookies';
import {
	canProfessionalCancelSession,
	canProfessionalManageP2PSession,
	getProfessionalSessionBookingSource,
} from '@/utils/sessionScheduledBy';
import { getSessionPatientDisplayName } from '../utils/getSessionPatientDisplayName';
import { formatSessionTimeRange } from '../utils/calendarLogic';

const STATUS_BADGE: Record<
	Sessions.SessionStatus,
	{ label: string; className: string }
> = {
	CREATED: {
		label: 'A pagar',
		className: 'border-amber-100 bg-amber-50 text-amber-700',
	},
	CONFIRMED: {
		label: 'Confirmado',
		className: 'border-emerald-100 bg-emerald-50 text-emerald-700',
	},
	STARTED: {
		label: 'Em andamento',
		className: 'border-orange-100 bg-orange-50 text-orange-700',
	},
	FINISHED: {
		label: 'Finalizada',
		className: 'border-zinc-200 bg-zinc-100 text-zinc-700',
	},
	CANCELLED: {
		label: 'Cancelada',
		className: 'border-red-100 bg-red-50 text-red-700',
	},
};

type ProfessionalHistorySessionCardProps = {
	session: Sessions.Session;
	onOpen: () => void;
	onCancelRequest: () => void;
};

const ProfessionalHistorySessionCard = ({
	session,
	onOpen,
	onCancelRequest,
}: ProfessionalHistorySessionCardProps) => {
	const navigate = useNavigate();
	const [isJoiningCall, setIsJoiningCall] = useState(false);
	const [isFooterActionHovered, setIsFooterActionHovered] = useState(false);
	const companyBanner = getSessionCallCompanyBanner(session);
	const isProfessionalAccessUnlocked =
		useProfessionalSessionAccessWindow(session);
	const canEnterOnlineSession =
		session.is_online &&
		(session.status === 'CONFIRMED' || session.status === 'STARTED');
	const canEnterTranscriptionOnlySession =
		isP2PTranscriptionOnlySessionType(session.type) &&
		(session.status === 'CONFIRMED' || session.status === 'STARTED');
	const canEnterSession =
		canEnterOnlineSession || canEnterTranscriptionOnlySession;

	const canManageSession =
		professionalHasP2PPermission(getProfessionalData()) &&
		canProfessionalManageP2PSession(session);
	const canCancelSession = canProfessionalCancelSession(session);

	const patientName = getSessionPatientDisplayName(session);
	const statusBadge = STATUS_BADGE[session.status];
	const dateLabel = new Date(session.selected_date).toLocaleDateString(
		'pt-BR',
	);
	const timeRange = formatSessionTimeRange(session.selected_date);

	const handleEnterClick = (event: MouseEvent) => {
		event.stopPropagation();
		if (!canEnterSession) return;

		if (!isProfessionalAccessUnlocked) {
			toast.info(
				'Só é possível entrar nos últimos 5 minutos antes do horário agendado.',
			);
			return;
		}

		void openProfessionalSessionCall({
			sessionId: session.id,
			navigate,
			sessionStatus: session.status,
			sessionType: session.type,
			isOnline: session.is_online,
			transcriptionAllowed: isSessionTranscriptionAllowed(session),
			transcriptionMock: session.transcription_mock === true,
			companyBanner,
			onStart: () => setIsJoiningCall(true),
			onFinally: () => setIsJoiningCall(false),
		});
	};

	const handleEditClick = (event: MouseEvent) => {
		event.stopPropagation();
		const encodedParams = buildProfessionalSessionEditUrlEncoded(session);
		if (!encodedParams) return;
		navigate(`/profissional/consultas/agendar?d=${encodedParams}`);
	};

	const handleCancelClick = (event: MouseEvent) => {
		event.stopPropagation();
		onCancelRequest();
	};

	const enterLabel = isJoiningCall
		? 'Entrando...'
		: isProfessionalAccessUnlocked
		  ? canEnterTranscriptionOnlySession
				? 'Transcrever'
				: 'Entrar'
		  : 'Em 5 min';

	const hasFooterActions =
		canEnterSession || canManageSession || canCancelSession;
	const bookingSource = getProfessionalSessionBookingSource(session);
	const emptyFooterMessage = !hasFooterActions
		? session.status === 'FINISHED'
			? 'Encerrado'
			: bookingSource === 'plumaa_user'
			  ? 'Consulta agendada por um usuário Plumaa'
			  : bookingSource === 'plumaa_team'
			    ? 'Consulta agendada pela equipe Plumaa'
			    : null
		: null;

	const handleFooterActionMouseEnter = () => setIsFooterActionHovered(true);
	const handleFooterActionMouseLeave = () => setIsFooterActionHovered(false);

	const footerChevron = (
		<ChevronRight
			className={cn(
				'size-4 shrink-0 text-stone-300 transition-colors',
				!isFooterActionHovered && 'group-hover:text-stone-800',
			)}
			aria-hidden
		/>
	);

	return (
		<div
			role="button"
			tabIndex={0}
			onClick={onOpen}
			onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					onOpen();
				}
			}}
			className={cn(
				'group w-full cursor-pointer rounded-2xl border border-stone-200 bg-white px-5 py-[1.125rem] text-left shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400',
				isFooterActionHovered
					? 'border-stone-200 bg-white'
					: 'hover:border-stone-400 hover:bg-stone-50',
			)}
		>
			<div className="flex items-start justify-between gap-2.5">
				<h4
					className={cn(
						'min-w-0 truncate text-[15px] font-bold leading-snug text-stone-900 transition-colors',
						!isFooterActionHovered && 'group-hover:text-stone-950',
					)}
				>
					{patientName}
				</h4>
				<span
					className={cn(
						'shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-bold',
						statusBadge.className,
					)}
				>
					{statusBadge.label}
				</span>
			</div>

			<div className="mt-2.5 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[13px] text-stone-400">
				<span className="inline-flex items-center gap-1.5">
					<Calendar className="size-4 shrink-0" aria-hidden />
					{dateLabel}
				</span>
				<span className="inline-flex items-center gap-1.5">
					<Clock className="size-4 shrink-0" aria-hidden />
					{timeRange}
				</span>
			</div>

			<SessionCancellationNotice
				session={session}
				viewer="professional"
				compact
				className="mt-2"
			/>

			<div className="mt-2.5 flex items-center gap-2.5 border-t border-stone-100 pt-2.5">
				{emptyFooterMessage ? (
					<>
						<p
							className={cn(
								'min-w-0 flex-1 text-[13px] leading-snug text-stone-400 transition-colors',
								!isFooterActionHovered &&
									'group-hover:text-stone-600',
							)}
						>
							{emptyFooterMessage}
						</p>
						{footerChevron}
					</>
				) : hasFooterActions ? (
					<>
						<div className="flex min-w-0 flex-1 items-center justify-start gap-2.5">
							{canEnterSession ? (
								<Button
									type="button"
									text={enterLabel}
									className={cn(
										'h-8 rounded-md border-none px-3.5 py-0 text-xs font-bold leading-none text-white shadow-sm transition-all',
										isJoiningCall ||
											!isProfessionalAccessUnlocked
											? 'cursor-not-allowed bg-zinc-300 hover:bg-zinc-300'
											: 'bg-zinc-900 hover:bg-black',
									)}
									disabled={
										isJoiningCall ||
										!isProfessionalAccessUnlocked
									}
									onMouseEnter={handleFooterActionMouseEnter}
									onMouseLeave={handleFooterActionMouseLeave}
									onClick={(event) => {
										event.stopPropagation();
										handleEnterClick(event);
									}}
								/>
							) : null}
							{canManageSession ? (
								<span
									role="button"
									tabIndex={0}
									onClick={handleEditClick}
									onMouseEnter={handleFooterActionMouseEnter}
									onMouseLeave={handleFooterActionMouseLeave}
									onKeyDown={(
										event: KeyboardEvent<HTMLSpanElement>,
									) => {
										if (
											event.key === 'Enter' ||
											event.key === ' '
										) {
											event.preventDefault();
											handleEditClick(
												event as unknown as MouseEvent<HTMLSpanElement>,
											);
										}
									}}
									className="inline-flex h-7 w-[4.75rem] shrink-0 cursor-pointer items-center justify-center rounded-md text-xs font-bold text-stone-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
								>
									Editar
								</span>
							) : null}
							{canCancelSession ? (
								<span
									role="button"
									tabIndex={0}
									onClick={handleCancelClick}
									onMouseEnter={handleFooterActionMouseEnter}
									onMouseLeave={handleFooterActionMouseLeave}
									onKeyDown={(
										event: KeyboardEvent<HTMLSpanElement>,
									) => {
										if (
											event.key === 'Enter' ||
											event.key === ' '
										) {
											event.preventDefault();
											handleCancelClick(
												event as unknown as MouseEvent<HTMLSpanElement>,
											);
										}
									}}
									className="inline-flex h-7 w-[4.75rem] shrink-0 cursor-pointer items-center justify-center rounded-md text-xs font-bold text-stone-400 transition-colors hover:bg-red-50 hover:text-red-600"
								>
									Cancelar
								</span>
							) : null}
						</div>

						{footerChevron}
					</>
				) : (
					<div className="flex w-full items-center justify-end">
						{footerChevron}
					</div>
				)}
			</div>
		</div>
	);
};

export { ProfessionalHistorySessionCard };
